// The activity catalog. Imported by both the browser and the Pages Function so
// the two cannot disagree about an activity's name or duration.
//
// Editing this file is the supported way to add, remove, rename or re-time an
// activity. No other file should need to change.
//
// Identifiers are the value exchanged between client and server, so they are
// stable: do not change one once an activity has been used, or a page loaded
// before the deploy will send an id the function no longer knows.

/** Display names are bounded so the composed Discord event name always fits. */
export const DISPLAY_NAME_MAX_LENGTH = 60;

/**
 * @typedef {object} Activity
 * @property {string} id              kebab-case, unique, stable
 * @property {string} name            shown to her; at most DISPLAY_NAME_MAX_LENGTH chars
 * @property {number} durationMinutes positive integer; sets the Discord event's end time
 *
 * Names are the whole label. No descriptions: she knows what these are, and a
 * sentence explaining "Minecraft" would be noise.
 */

/** @type {readonly Activity[]} */
export const ACTIVITIES = Object.freeze([
  { id: 'movie-night',     name: 'Movie night',             durationMinutes: 150 },
  { id: 'anime-night',     name: 'Anime night',             durationMinutes: 120 },
  { id: 'stardew',         name: 'Stardew Valley',          durationMinutes: 120 },
  { id: 'minecraft',       name: 'Minecraft',               durationMinutes: 120 },
  { id: 'double-up',       name: 'Double Up',               durationMinutes: 90  },
  { id: 'clay-date',       name: 'Clay date',               durationMinutes: 90  },
  { id: 'crochet',         name: 'Teach me how to crochet', durationMinutes: 90  },
  { id: 'same-recipe',     name: 'Same recipe',             durationMinutes: 90  },
  { id: 'just-yapping',    name: 'Just yapping',            durationMinutes: 120 },
  // The escape hatch. Its name comes from her rather than from here, which is
  // the one and only case where the server takes an event name from the client.
  { id: 'other',           name: 'Something else',          durationMinutes: 120, custom: true },
]);

/** The id of the write-your-own entry. */
export const CUSTOM_ACTIVITY_ID = 'other';

/** The longest a written-in activity may be, matching the catalog's own bound. */
export const CUSTOM_NAME_MAX = DISPLAY_NAME_MAX_LENGTH;

/**
 * Validate and tidy a written-in activity name.
 *
 * @param {unknown} raw
 * @returns {{ok: true, name: string} | {ok: false, reason: 'empty' | 'too-long'}}
 */
export function normaliseCustomName(raw) {
  if (typeof raw !== 'string') return { ok: false, reason: 'empty' };
  // Collapse whitespace, including newlines — a Discord event name is one line.
  const name = raw.replace(/\s+/g, ' ').trim();
  if (name.length === 0) return { ok: false, reason: 'empty' };
  if (name.length > CUSTOM_NAME_MAX) return { ok: false, reason: 'too-long' };
  return { ok: true, name };
}

const KEBAB_CASE = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

/**
 * @typedef {object} CatalogProblem
 * @property {number|null} index index in the catalog, or null for whole-catalog problems
 * @property {string|null} id    the offending entry's id where it has one
 * @property {string} message    what is wrong, naming the entry
 */

/**
 * Validate a catalog. Called at page load and at function cold start, not only
 * from tests, so that a catalog edited and deployed without running the test
 * suite fails visibly rather than silently serving the wrong activity.
 *
 * @param {readonly Activity[]} activities
 * @returns {{ok: true} | {ok: false, problems: CatalogProblem[]}}
 */
export function validateCatalog(activities = ACTIVITIES) {
  /** @type {CatalogProblem[]} */
  const problems = [];

  if (!Array.isArray(activities) || activities.length === 0) {
    problems.push({
      index: null,
      id: null,
      message: 'the catalog is empty; at least one activity is required',
    });
    return { ok: false, problems };
  }

  const seen = new Map();

  activities.forEach((activity, index) => {
    const label = activity && typeof activity.id === 'string' && activity.id
      ? `"${activity.id}"`
      : `entry at index ${index}`;

    if (!activity || typeof activity !== 'object') {
      problems.push({ index, id: null, message: `${label} is not an object` });
      return;
    }

    const { id, name, durationMinutes } = activity;

    if (typeof id !== 'string' || id.length === 0) {
      problems.push({ index, id: null, message: `${label} has no id` });
    } else if (!KEBAB_CASE.test(id)) {
      problems.push({ index, id, message: `${label} is not a kebab-case id` });
    } else if (seen.has(id)) {
      problems.push({
        index,
        id,
        message: `${label} duplicates the id already used by the entry at index ${seen.get(id)}`,
      });
    } else {
      seen.set(id, index);
    }

    if (typeof name !== 'string' || name.trim().length === 0) {
      problems.push({ index, id: id ?? null, message: `${label} has no display name` });
    } else if (name.length > DISPLAY_NAME_MAX_LENGTH) {
      problems.push({
        index,
        id: id ?? null,
        message: `${label} has a display name of ${name.length} characters, over the ${DISPLAY_NAME_MAX_LENGTH} allowed`,
      });
    }

    if (typeof durationMinutes !== 'number' || !Number.isFinite(durationMinutes)) {
      problems.push({ index, id: id ?? null, message: `${label} has no numeric duration` });
    } else if (!Number.isInteger(durationMinutes)) {
      problems.push({
        index,
        id: id ?? null,
        message: `${label} has a non-integer duration of ${durationMinutes} minutes`,
      });
    } else if (durationMinutes <= 0) {
      problems.push({
        index,
        id: id ?? null,
        message: `${label} has a duration of ${durationMinutes} minutes, which must be positive`,
      });
    }
  });

  return problems.length === 0 ? { ok: true } : { ok: false, problems };
}

/** Throwing form, for the page-load and cold-start call sites. */
export function assertCatalogValid(activities = ACTIVITIES) {
  const result = validateCatalog(activities);
  if (!result.ok) {
    const detail = result.problems.map((p) => p.message).join('; ');
    throw new Error(`activity catalog is invalid: ${detail}`);
  }
}

/**
 * Resolve an activity id to its entry. Returns an explicit not-found result
 * rather than throwing or returning undefined, because the server treats an
 * unknown id as a bad request and the browser treats it as "reload the page":
 * both need to branch on it, neither wants an exception.
 *
 * @param {string} id
 * @param {readonly Activity[]} [activities]
 * @returns {{found: true, activity: Activity} | {found: false, id: string}}
 */
export function findActivity(id, activities = ACTIVITIES) {
  if (typeof id !== 'string' || id.length === 0) {
    return { found: false, id: String(id) };
  }
  const activity = activities.find((candidate) => candidate.id === id);
  return activity ? { found: true, activity } : { found: false, id };
}
